import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
} from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const WarehouseContext = createContext();

export const useWarehouse = () => {
  const context = useContext(WarehouseContext);
  if (!context) {
    throw new Error("useWarehouse must be used within a WarehouseProvider");
  }

  const fetchOptions = async (inputValue) => {
    const response = await getRequest(`/warehouses?search=${inputValue}`);
    if (response.success) {
      return response.data.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
      }));
    }
    return [];
  };

  return { ...context, fetchOptions };
};

export const WarehouseProvider = ({ children }) => {
  const [warehouses, setWarehouses] = useState({
    data: [],
    pagination: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
  });

  const [warehouseStats] = useState({
    total: 0,
    active: 0,
    loading: false,
    error: null,
  });

  const queryString = buildQueryString({
    search: filterOptions.search || "",
    page: filterOptions.currentPage,
    limit: 10,
  });

  const fetchWarehouses = useCallback(async () => {
    setWarehouses((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`/warehouses${queryString}`);
      if (response.success) {
        setWarehouses({
          data: response.data,
          pagination: response.pagination,
          error: null,
          loading: false,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.pagination?.last_page,
        }));
      }
    } catch (error) {
      setWarehouses({
        data: [],
        pagination: null,
        error: error.message,
        loading: false,
      });
    }
  }, [queryString]);

  // Initial load and when dependencies change
  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = warehouses.pagination?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  const createWarehouse = useCallback(
    async (warehouseData) => {
      try {
        const response = await postRequest("/warehouses", warehouseData);
        if (response.success) {
          fetchWarehouses(); // Refresh the list
          return { success: true };
        }
        return { success: false, error: response.message };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [fetchWarehouses]
  );

  const updateWarehouse = useCallback(
    async (id, warehouseData) => {
      try {
        const response = await patchRequest(`/warehouses/${id}`, warehouseData);
        if (response.success) {
          fetchWarehouses(); // Refresh the list
          return { success: true };
        }
        return { success: false, error: response.message };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [fetchWarehouses]
  );

  const deleteWarehouse = useCallback(
    async (id) => {
      try {
        const response = await deleteRequest(`/warehouses/${id}`);
        if (response.success) {
          fetchWarehouses(); // Refresh the list
          return { success: true };
        }
        return { success: false, error: response.message };
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [fetchWarehouses]
  );

  const value = {
    warehouses: warehouses.data,
    loading: warehouses.loading,
    error: warehouses.error,
    pagination: warehouses.pagination,
    warehouseStats,
    refetch: fetchWarehouses,
    filterOptions,
    handleSearch,
    createWarehouse,
    updateWarehouse,
    deleteWarehouse,
    Pagination,
  };

  return (
    <WarehouseContext.Provider value={value}>
      {children}
    </WarehouseContext.Provider>
  );
};
